// this whole page is only rendered on the checks route
/* eslint-disable local/no-direct-route-config-access */
import { gql, useQuery } from '@apollo/client';
import IconBranch from '@apollo/icons/default/IconBranch.svg';
import IconMetrics from '@apollo/icons/default/IconMetrics.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import IconTeam from '@apollo/icons/default/IconTeam.svg';
import IconVariant from '@apollo/icons/default/IconVariant.svg';
import { Button, ButtonGroup } from '@apollo/orbit';
import { sortBy } from 'lodash';
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import {
  proposalChecksListRouteConfig,
  recentChecksRouteConfig,
} from 'src/app/graph/routes';
import { FilterButton } from 'src/components/metricsFilter/filterButton/FilterButton';
import { MultiSelect } from 'src/components/multiSelect/MultiSelect';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';

const checksFiltersQuery = gql`
  query ChecksFiltersQuery(
    $graphId: ID!
    $authorsFilter: CheckFilterInput
    $subgraphsFilter: CheckFilterInput
    $branchesFilter: CheckFilterInput!
    $includeVariants: Boolean!
  ) {
    me {
      id
    }
    service(id: $graphId) {
      id
      checksAuthorOptions(filter: $authorsFilter)
      checksCreatedByOptions(filter: $authorsFilter) {
        id
        name
        ... on User {
          fullName
        }
      }
      checksSubgraphOptions(filter: $subgraphsFilter)
      checksBranchOptions(filter: $branchesFilter)
      variants @include(if: $includeVariants) {
        id
        name
      }
    }
  }
`;

const statusOptions = new Map([
  [GraphQLTypes.CheckFilterInputStatusOption.FAILED, 'Failed'],
  [GraphQLTypes.CheckFilterInputStatusOption.PENDING, 'Pending'],
  [GraphQLTypes.CheckFilterInputStatusOption.PASSED, 'Passed'],
]);

export const ChecksFilters = ({ className }: { className?: string }) => {
  const location = useLocation();
  const history = useHistory();

  const { isProposalRoute } = useInternalGraphLinking();

  const {
    graphId,
    graphVariant,
    author,
    createdBy,
    branch,
    status,
    subgraph,
    variants,
    view,
  } = useRouteParams(recentChecksRouteConfig, proposalChecksListRouteConfig);

  const hasFilters = Boolean(
    author || createdBy || branch || subgraph || status || variants,
  );
  const selectedAuthors =
    typeof author === 'string' ? [author] : (author ?? []);
  const selectedCreatedBy =
    typeof createdBy === 'string' ? [createdBy] : (createdBy ?? []);
  const selectedBranches = typeof branch === 'string' ? [branch] : branch;
  const selectedSubgraphs =
    typeof subgraph === 'string' ? [subgraph] : subgraph;
  const selectedVariants = typeof variants === 'string' ? [variants] : variants;

  const AUTHOR_PREFIX = 'author:';
  const CREATED_BY_PREFIX = 'createdBy:';

  const authorsFilter: GraphQLTypes.InputMaybe<GraphQLTypes.CheckFilterInput> =
    {
      status,
      includeProposalChecks: isProposalRoute,
      ...(isProposalRoute ? { variants: [graphVariant] } : {}),
    };
  const branchesFilter = {
    ...(selectedAuthors?.length ? { authors: selectedAuthors } : {}),
    ...(isProposalRoute ? { variants: [graphVariant] } : {}),
    status,
  };
  const subgraphsFilter: GraphQLTypes.InputMaybe<GraphQLTypes.CheckFilterInput> =
    {
      status,
      includeProposalChecks: isProposalRoute,
      ...(isProposalRoute ? { variants: [graphVariant] } : {}),
    };

  const { data } = useQuery<
    GraphQLTypes.ChecksFiltersQuery,
    GraphQLTypes.ChecksFiltersQueryVariables
  >(checksFiltersQuery, {
    variables: {
      graphId,
      authorsFilter,
      branchesFilter,
      subgraphsFilter,
      includeVariants: !isProposalRoute,
    },
  });

  const allAuthors = sortBy(data?.service?.checksAuthorOptions ?? []);
  const allCreatedBy = sortBy(data?.service?.checksCreatedByOptions ?? []);
  const allAuthorsAndCreatedBy = [...allAuthors, ...allCreatedBy];
  const allBranches = sortBy(data?.service?.checksBranchOptions ?? []);
  const allSubgraphs = sortBy(data?.service?.checksSubgraphOptions ?? []);
  const allVariants = sortBy(data?.service?.variants?.map((v) => v.name) ?? []);

  const selectedAuthorAndCreatedByValues = new Set([
    ...selectedCreatedBy.map((x) => CREATED_BY_PREFIX + x),
    ...selectedAuthors.map((x) => AUTHOR_PREFIX + x),
  ]);

  const authorsTriggerValue =
    !selectedAuthorAndCreatedByValues ||
    selectedAuthorAndCreatedByValues.size === 0
      ? `All Authors`
      : `${selectedAuthorAndCreatedByValues.size} ${format.pluralize(
          selectedAuthorAndCreatedByValues.size,
          'Author',
        )}`;

  const branchesTriggerValue =
    !selectedBranches || selectedBranches.length === 0
      ? `All Branches`
      : `${selectedBranches.length} ${format.pluralize(
          selectedBranches.length,
          'Branch',
          'es',
        )}`;
  const subgraphsTriggerValue =
    !selectedSubgraphs || selectedSubgraphs.length === 0
      ? `All Subgraphs`
      : `${selectedSubgraphs.length} ${format.pluralize(
          selectedSubgraphs.length,
          'Subgraph',
        )}`;
  const variantsTriggerValue =
    !selectedVariants || selectedVariants.length === 0
      ? `All Variants`
      : `${selectedVariants.length} ${format.pluralize(
          selectedVariants.length,
          'Variant',
        )}`;
  const isVariantTab = view === 'variant';

  const allAuthorAndCreatedByValues = allAuthorsAndCreatedBy.map((x) =>
    typeof x === 'string'
      ? `${AUTHOR_PREFIX}${x}`
      : `${CREATED_BY_PREFIX}${x.id}`,
  );

  return (
    <ButtonGroup className={className}>
      <MultiSelect
        valueTitle="Status"
        allValues={Array.from(statusOptions.keys())}
        selectedValues={new Set(status ? [status] : undefined)}
        setSelectedValues={(selectedValues) => {
          const arrValues = Array.from(selectedValues.values());
          const newRouteValues = {
            location,
            patch: {
              // As we only have 1 option in here, mimic the behavior of a
              // radio button here rather than a multi-select.
              status: isMinLengthArray(1, arrValues)
                ? arrValues[0]
                : RouteConfig.DELETE,
            },
          };
          history.push(
            isProposalRoute
              ? proposalChecksListRouteConfig.locationFrom(newRouteValues)
              : recentChecksRouteConfig.locationFrom(newRouteValues),
          );
        }}
        triggerNode={
          <FilterButton
            variant={isProposalRoute ? 'secondary' : 'hidden'}
            leftIcon={<IconMetrics />}
          >
            {status ? statusOptions.get(status) : 'Any Status'}
          </FilterButton>
        }
        selectionMode="single"
        labelForValue={(value) => statusOptions.get(value) ?? value}
      />
      {!isVariantTab && !isProposalRoute ? (
        <MultiSelect
          className="max-h-120 overflow-y-auto"
          valueTitle="Variants"
          allValues={allVariants}
          selectedValues={new Set(selectedVariants)}
          setSelectedValues={(selectedValues) => {
            history.push(
              recentChecksRouteConfig.locationFrom({
                location,
                patch: { variants: Array.from(selectedValues) },
              }),
            );
          }}
          triggerNode={
            <FilterButton variant="hidden" leftIcon={<IconVariant />}>
              {variantsTriggerValue}
            </FilterButton>
          }
        />
      ) : null}
      <MultiSelect
        className="max-h-120 overflow-y-auto"
        valueTitle="Subgraph"
        allValues={allSubgraphs}
        selectedValues={new Set(selectedSubgraphs)}
        setSelectedValues={(selectedValues) => {
          const newRouteValues = {
            location,
            patch: { subgraph: Array.from(selectedValues) },
          };
          history.push(
            isProposalRoute
              ? proposalChecksListRouteConfig.locationFrom(newRouteValues)
              : recentChecksRouteConfig.locationFrom(newRouteValues),
          );
        }}
        triggerNode={
          <FilterButton
            variant={isProposalRoute ? 'secondary' : 'hidden'}
            leftIcon={<IconSingleService />}
          >
            {subgraphsTriggerValue}
          </FilterButton>
        }
      />
      {!isProposalRoute && (
        <span className="col-start-1 xl:col-start-auto">
          <MultiSelect
            className="max-h-120 overflow-y-auto"
            valueTitle="Branch"
            allValues={allBranches}
            selectedValues={new Set(selectedBranches)}
            setSelectedValues={(selectedValues) => {
              history.push(
                recentChecksRouteConfig.locationFrom({
                  location,
                  patch: { branch: Array.from(selectedValues) },
                }),
              );
            }}
            triggerNode={
              <FilterButton variant="hidden" leftIcon={<IconBranch />}>
                {branchesTriggerValue}
              </FilterButton>
            }
          />
        </span>
      )}
      <MultiSelect
        className="max-h-120 overflow-y-auto"
        valueTitle="Author"
        allValues={allAuthorAndCreatedByValues}
        selectedValues={selectedAuthorAndCreatedByValues}
        labelForValue={(value) => {
          if (value.startsWith(AUTHOR_PREFIX)) {
            return value.split(AUTHOR_PREFIX)[1] ?? 'Unknown';
          } else {
            const createdByObj = allCreatedBy.find(
              (x) => x.id === value.split(CREATED_BY_PREFIX)[1],
            );
            return createdByObj?.__typename === 'User'
              ? (createdByObj?.fullName ?? 'Unknown')
              : 'Unknown';
          }
        }}
        setSelectedValues={(selectedValues) => {
          const newSelectedAuthors = [...selectedValues]
            .filter((x) => x.startsWith(AUTHOR_PREFIX))
            .map((x) => x.split(AUTHOR_PREFIX)[1] ?? 'Unknown');
          const newSelectedCreatedBy = [...selectedValues]
            .filter((x) => x.startsWith(CREATED_BY_PREFIX))
            .map((x) => x.split(CREATED_BY_PREFIX)[1] ?? 'Unknown');

          const newRouteValues = {
            location,
            patch: {
              createdBy: newSelectedCreatedBy,
              author: newSelectedAuthors,
            },
          };
          history.push(
            isProposalRoute
              ? proposalChecksListRouteConfig.locationFrom(newRouteValues)
              : recentChecksRouteConfig.locationFrom(newRouteValues),
          );
        }}
        triggerNode={
          <FilterButton
            variant={isProposalRoute ? 'secondary' : 'hidden'}
            leftIcon={<IconTeam />}
          >
            {authorsTriggerValue}
          </FilterButton>
        }
      />
      <Button
        variant={hasFilters ? 'secondary' : 'hidden'}
        size="sm"
        isDisabled={!hasFilters}
        as={Link}
        to={
          isProposalRoute
            ? proposalChecksListRouteConfig.locationFrom({
                location,
                patch: {
                  author: RouteConfig.DELETE,
                  createdBy: RouteConfig.DELETE,
                  subgraph: RouteConfig.DELETE,
                  status: RouteConfig.DELETE,
                },
              })
            : recentChecksRouteConfig.locationFrom({
                location,
                patch: {
                  author: RouteConfig.DELETE,
                  createdBy: RouteConfig.DELETE,
                  subgraph: RouteConfig.DELETE,
                  branch: RouteConfig.DELETE,
                  status: RouteConfig.DELETE,
                  variants: RouteConfig.DELETE,
                },
              })
        }
      >
        Reset
      </Button>
    </ButtonGroup>
  );
};
